import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { AiOutlineLogout } from 'react-icons/ai'; // Used for logout button
import { useDispatch } from 'react-redux';
import { changeThemeToDark, changeThemeToLight } from '@/redux/actions/themeActions';
import { useHistory } from 'react-router-dom';
import toast from 'react-hot-toast';
import CryptoJS from 'crypto-js';

import TopbarProfile from '../components/topbar/TopbarProfile';
import TopbarSidebarButton from '../components/topbar/TopbarSidebarButton';
import {
  TopbarContainer,
  TopbarLeft,
  TopbarLogo,
  TopbarRight,
  TopbarRightOver,
} from '../components/topbar/BasicTopbarComponents';

import { AuthContext } from '../../../context/AuthContext';
import { GlobalContext } from '../../../context/GlobalContext';
import { ThemeContext } from '../../../context/ThemeContext';

// Add custom CSS for responsive styling
const responsiveStyles = {
  rightContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '10px',
  },
  currencyDropdown: {
    padding: '5px 10px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    backgroundColor: 'white',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  logoutButton: {
    color: 'red',
    background: 'transparent',
    border: 'none',
    padding: '5px',
    cursor: 'pointer',
  },
  logoutIcon: {
    fontSize: '24px',
    transition: 'transform 0.3s ease',
  },
};

// Media query effects will be handled in useEffect with resize listener
const Topbar = ({ changeMobileSidebarVisibility, changeSidebarVisibility }) => {
  const [isToggled, setIsToggled] = useState(false); // Toggle for theme switch
  const {
    setIsLoggedIn, currencyType, type, setCurrencyType, agentId,
  } = useContext(AuthContext);
  const { apiGlobalforlogin, apiGlobal } = useContext(GlobalContext);
  const { setCurrentTheme } = useContext(ThemeContext);
  const [currency, setCurrency] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const history = useHistory();
  const dispatch = useDispatch();

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Adjust dropdown styles based on window width
  const getDropdownStyles = () => ({
      ...responsiveStyles.currencyDropdown,
      width: windowWidth < 768 ? '80px' : '150px',
      fontSize: windowWidth < 768 ? '11px' : '14px',
      padding: windowWidth < 768 ? '4px 6px' : '5px 10px',
      // For extremely small devices, add more adjustments
      ...(windowWidth < 480 && {
        width: '70px',
        fontSize: '10px',
        padding: '3px 4px',
      }),
    });

  // Toggle theme and update global theme state
  const handleToggle = () => {
    const newToggleState = !isToggled;
    setIsToggled(newToggleState);

    if (!newToggleState) {
      dispatch(changeThemeToDark());
      setCurrentTheme('light');
    } else {
      dispatch(changeThemeToLight());
      setCurrentTheme('Dark');
    }
  };

  // Logout logic with API call
  const onlogoutHandler = async () => {
    try {
      const res = await fetch(`${apiGlobalforlogin}/agent/logout.php`);
      const data = await res.json();

      if (data.loggedOut === true) {
        toast.success('Logout Successful. Redirecting...');
        setIsLoggedIn(false);
        localStorage.clear();
        setTimeout(() => history.push('/'), 2000);
      } else {
        toast.error('Something went wrong during logout.');
      }
    } catch (err) {
      toast.error('Logout failed. Please try again.');
      console.error('Logout Error:', err);
    }
  };

  // Currency dropdown handler
  // const handleCurrencyChange = async (e) => {
  //   const { value } = e.target;
 
  //   const formdata = new FormData();
  //   formdata.append('currency_id', value);
  //   formdata.append('agent_id', agentId);

  //   try {
  //     const url = await fetch(`${apiGlobal}/agent/update_currency.php`, {
  //       method: 'POST',
  //       body: formdata,
  //     });
  //     const response = await url.json();
  //     if (response.status && response.message === 'Success') {
  //       setCurrencyType(value);
  //       toast.success('Currency set successfully..');
  //     } else {
  //       setCurrencyType('0');
  //       toast.success('unable to set Currency');
  //     }
  //   } catch (error) {
  //     console.log(error);
  //   }

  //   // Encrypt selected currency before storing
  //   const encryptedCurrencyType = CryptoJS.AES
  //     .encrypt(JSON.stringify(value), 'T4$Bv9#Aa@QWz8m&5!Lpo1kHsTz4jL2')
  //     .toString();

  //   localStorage.setItem('78582826978678984898069', encryptedCurrencyType);
  // };

  const fetchCurrencyList = async () => {
    try {
      const url = await fetch(`${apiGlobal}/currency_master_list.php`, {
          method: 'POST',
        });
      const response = await url.json();
      if (response.status && response.message === 'Success') {
        setCurrency(response?.data);
      } else {
        setCurrency([]);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchCurrencyList();
  }, []);

  return (
    <TopbarContainer>
      <TopbarLeft>
        <TopbarSidebarButton
          onClickMobile={changeMobileSidebarVisibility}
          onClickDesktop={changeSidebarVisibility}
        />
        <TopbarLogo to="/dashboard" />
      </TopbarLeft>

      <TopbarRight>
        <TopbarRightOver
          style={{
            ...responsiveStyles.rightContainer,
            // On very small screens, decrease the gap
            gap: windowWidth < 480 ? '5px' : '10px',
          }}
        >
          {/* Currency Dropdown */}
          {/* <select
            style={getDropdownStyles()}
            onChange={handleCurrencyChange}
            value={currencyType}
            disabled={type !== '0'}
          >
            <option value="0">{windowWidth < 480 ? 'Currency' : 'Select Currency'}</option>
            {/* eslint-disable-next-line */}
          {/* {currency.map((curr, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <option key={index} value={curr?.id}>
                {windowWidth < 768 
                  ? `${curr?.currency_name}` 
                  : `${curr?.currency_name}-${curr?.country_id}`}
              </option>
            ))}
          </select> */}

          {/* Profile Component */}
          <TopbarProfile />

          {/* Logout Button */}
          <button
            type="button"
            onClick={onlogoutHandler}
            style={responsiveStyles.logoutButton}
            aria-label="Logout"
          >
            <AiOutlineLogout 
              style={{
                ...responsiveStyles.logoutIcon,
                fontSize: windowWidth < 480 ? '20px' : '24px',
              }} 
            />
          </button>
        </TopbarRightOver>
      </TopbarRight>
    </TopbarContainer>
  );
};

// PropTypes
Topbar.propTypes = {
  changeMobileSidebarVisibility: PropTypes.func.isRequired,
  changeSidebarVisibility: PropTypes.func.isRequired,
};

export default Topbar;




// import React, { useState, useContext, useEffect } from 'react';
// import PropTypes from 'prop-types';
// import { AiOutlineLogout } from 'react-icons/ai'; // Used for logout button
// import { useDispatch } from 'react-redux';
// import { changeThemeToDark, changeThemeToLight } from '@/redux/actions/themeActions';
// import { useHistory } from 'react-router-dom';
// import toast from 'react-hot-toast';
// import CryptoJS from 'crypto-js';

// import TopbarProfile from '../components/topbar/TopbarProfile';
// import TopbarSidebarButton from '../components/topbar/TopbarSidebarButton';
// import {
//   TopbarContainer,
//   TopbarLeft,
//   TopbarLogo,
//   TopbarRight,
//   TopbarRightOver,
// } from '../components/topbar/BasicTopbarComponents';

// import { AuthContext } from '../../../context/AuthContext';
// import { GlobalContext } from '../../../context/GlobalContext';
// import { ThemeContext } from '../../../context/ThemeContext';

// const Topbar = ({ changeMobileSidebarVisibility, changeSidebarVisibility }) => {
//   const [isToggled, setIsToggled] = useState(false); // Toggle for theme switch
//   const {
//  setIsLoggedIn, currencyType, type, setCurrencyType, agentId,
// } = useContext(AuthContext);
//   const { apiGlobalforlogin, apiGlobal } = useContext(GlobalContext);
//   const { setCurrentTheme } = useContext(ThemeContext);
//   const [currency, setCurrency] = useState([]);

//   const history = useHistory();
//   const dispatch = useDispatch();

//   // Toggle theme and update global theme state
//   const handleToggle = () => {
//     const newToggleState = !isToggled;
//     setIsToggled(newToggleState);

//     if (!newToggleState) {
//       dispatch(changeThemeToDark());
//       setCurrentTheme('light');
//     } else {
//       dispatch(changeThemeToLight());
//       setCurrentTheme('Dark');
//     }
//   };

//   // Logout logic with API call
//   const onlogoutHandler = async () => {
//     try {
//       const res = await fetch(`${apiGlobalforlogin}/agent/logout.php`);
//       const data = await res.json();

//       if (data.loggedOut === true) {
//         toast.success('Logout Successful. Redirecting...');
//         setIsLoggedIn(false);
//         localStorage.clear();
//         setTimeout(() => history.push('/'), 2000);
//       } else {
//         toast.error('Something went wrong during logout.');
//       }
//     } catch (err) {
//       toast.error('Logout failed. Please try again.');
//       console.error('Logout Error:', err);
//     }
//   };

//   // Currency dropdown handler
//   const handleCurrencyChange = async (e) => {
//     const { value } = e.target;
 
//     const formdata = new FormData();
//     formdata.append('currency_id', value);
//     formdata.append('agent_id', agentId);

//     try {
//         const url = await fetch(`${apiGlobal}/agent/update_currency.php`, {
//           method: 'POST',
//           body: formdata,
//         });
//         const response = await url.json();
//         if (response.status && response.message === 'Success') {
//           setCurrencyType(value);
//           toast.success('Currency set successfully..');
//         } else {
//           setCurrencyType('0');
//           toast.success('unable to set Currency');
//         }
//     } catch (error) {
//       console.log(error);
//     }

//     // Encrypt selected currency before storing
//     const encryptedCurrencyType = CryptoJS.AES
//       .encrypt(JSON.stringify(value), 'T4$Bv9#Aa@QWz8m&5!Lpo1kHsTz4jL2')
//       .toString();

//     localStorage.setItem('78582826978678984898069', encryptedCurrencyType);
//   };



//   const fetchCurrencyList = async () => {
//     try {
//       const url = await fetch(`${apiGlobal}/currency_master_list.php`);
//       const response = await url.json();
//           if (response.status && response.message === 'Success') {
//             setCurrency(response?.data);
//           } else {
//             setCurrency([]);
//           }
//     } catch (error) {
//          console.log(error);
//     }
//   };

//   useEffect(() => {
//     fetchCurrencyList();
//   }, []);

//   return (
//     <TopbarContainer>
//       <TopbarLeft>
//         <TopbarSidebarButton
//           onClickMobile={changeMobileSidebarVisibility}
//           onClickDesktop={changeSidebarVisibility}
//         />
//         <TopbarLogo to="/online_marketing_dashboard" />
//       </TopbarLeft>

//       <TopbarRight>
//         <TopbarRightOver
//           style={{
//             display: 'flex',
//             justifyContent: 'flex-end',
//             alignItems: 'center',
//             gap: '10px',
//           }}
//         >
//           {/* Currency Dropdown */}
//           <select
//             style={{
//               padding: '5px 10px',
//               borderRadius: '4px',
//               border: '1px solid #ddd',
//               backgroundColor: 'white',
//               // display: window.innerWidth < 768 && 'none',
//               fontSize: window.innerWidth < 768 ? '12px' : '14px',
//               cursor: 'pointer',
//               maxWidth: window.innerWidth < 768 ? '120px' : 'auto',
//             }}
//             onChange={handleCurrencyChange}
//             value={currencyType}
//             disabled={type !== '0'}
//           >
//             <option value="0">Select Currency</option>
//             {/* eslint-disable-next-line */}
//             {currency.map((curr, index) => <option key={index} 
// value={curr?.id}>{curr?.currency_name}-{curr?.country_id}</option>)}
//           </select>

//           {/* Profile Component */}
//           <TopbarProfile />

//           {/* Logout Button */}
//           <button
//             type="button"
//             onClick={onlogoutHandler}
//             style={{
//               color: 'red',
//               background: 'transparent',
//               border: 'none',
//               padding: '5px',
//               cursor: 'pointer',
//             }}
//             aria-label="Logout"
//           >
//             <AiOutlineLogout style={{ fontSize: '24px', transition: 'transform 0.3s ease' }} />
//           </button>
//         </TopbarRightOver>
//       </TopbarRight>
//     </TopbarContainer>
//   );
// };

// // PropTypes
// Topbar.propTypes = {
//   changeMobileSidebarVisibility: PropTypes.func.isRequired,
//   changeSidebarVisibility: PropTypes.func.isRequired,
// };

// export default Topbar;

